import React, { useState, useEffect, useMemo } from "react";
import { Container, Row, Col, Button } from "react-bootstrap";
import Dropdown from "react-bootstrap/Dropdown";
import { jwtDecode } from "jwt-decode";
import { getUserPracticeById } from "../api";
import AccountList from "../Components/Accounts/AccountList";
import AgedInvoicesList from "../Components/Accounts/InvoiceList";
import TransactionList from "../Components/Accounts/TransactionList";
import ClaimList from "../Components/Accounts/ClaimList";
import ERAList from "../Components/Accounts/ERAList";
import AllocationReport from "../Components/Accounts/AllocationReport";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import "../Components/Accounts/acc-rail.css";

function Billing() {
  const [practice, setPractice] = useState([]);
  const [userRole, setUserRole] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Get active section from URL, default to "account"
  const activeSection = searchParams.get("section") || "account";
  // ERA view filter (Full List / Matched / Unmatched), read from the URL so
  // the ERAList picks it up and the rail dropdown can highlight the active one.
  const eraFilter = (searchParams.get("eraFilter") || "all").toLowerCase();
  // Which rail submenu is open (namespaced by rail so mobile + desktop don't collide)
  const [openSubKey, setOpenSubKey] = useState(null);

  // sections config: `label` drives the page heading (full name); `short`
  // is the very-small caption rendered under the icon in the rail, sized to
  // fit the 64px container.
  const sidebarItems = useMemo(
    () => [
      { key: "account", label: "Account List", short: "Accounts", icon: "fa-light fa-list" },
      {
        key: "invoices",
        label: "Open Invoices",
        short: "Invoices",
        gated: true,
        icon: "fa-sharp fa-light fa-file-invoice",
      },
      {
        key: "transactions",
        label: "Sales Report",
        short: "Sales",
        gated: true,
        icon: "fa-light fa-cart-arrow-down",
      },
      {
        key: "allocations",
        label: "Allocation Report",
        short: "Receipts",
        gated: true,
        icon: "fa-sharp fa-light fa-ballot-check",
      },
      {
        key: "claims",
        label: "Claims",
        short: "Claims",
        gated: true,
        icon: "fa-sharp fa-light fa-file-circle-exclamation",
      },
      {
        key: "eras",
        label: "ERA",
        short: "ERA's",
        gated: true,
        icon: "fa-sharp fa-light fa-atom",
        // Rail dropdown: switch the ERAList view between the full list and the
        // matched-only / unmatched-only slices (via the ?eraFilter URL param).
        type: "submenu",
        children: [
          { key: "all", label: "Full List", icon: "fa-solid fa-list" },
          // Unmatched doubles as the to-do list: those ERAs are the ones
          // still waiting to be matched or dismissed.
          { key: "matched", label: "Matched", icon: "fa-solid fa-circle-check" },
          { key: "unmatched", label: "Unmatched", icon: "fa-solid fa-circle-exclamation" },
        ],
      },
      // { key: "payments", label: "Ozow Payments", gated: true, icon: "fa-light fa-money-bill-wave" },
    ],
    [],
  );

  // Function to change section via URL
  const setActiveSection = (section) => {
    navigate(`/billing?section=${section}`, { replace: true });
  };

  // Handle legacy URL params (aging, paymentStatus, claims, eras)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("aging") && !params.get("section")) {
      navigate("/billing?section=invoices", { replace: true });
    }
    if (params.get("paymentStatus") && !params.get("section")) {
      navigate("/billing?section=payments", { replace: true });
    }
    if (params.get("claims") && !params.get("section")) {
      navigate("/billing?section=claims", { replace: true });
    }
    if (params.get("eras") && !params.get("section")) {
      navigate("/billing?section=eras", { replace: true });
    }
  }, [location.search, navigate]);

  // load user + roles
  useEffect(() => {
    async function getData() {
      try {
        const token = sessionStorage.getItem("user");
        if (!token) return;

        const user = jwtDecode(token);

        const rolesFromRole = Array.isArray(user?.role)
          ? user.role
          : user?.role
            ? [user.role]
            : [];
        const rolesFromPermissions = Array.isArray(user?.permissions)
          ? user.permissions
          : [];
        const roles = [...new Set([...rolesFromRole, ...rolesFromPermissions])];
        const allowedRoles = ["Finance", "SuperAdmin"];
        const hasAllowedRole = roles.some((r) => allowedRoles.includes(r));
        setUserRole(hasAllowedRole);

        const fetchedPractices = await getUserPracticeById(user._id);
        setPractice(Array.isArray(fetchedPractices) ? fetchedPractices : []);
      } catch (err) {
        console.error("⛔ Error fetching practices/roles:", err);
        setPractice([]);
        setUserRole(false);
      }
    }
    getData();
  }, []);

  // Render a single rail item for either rail. Plain sections render as a
  // Button that switches section; a `type: "submenu"` item (the ERA menu)
  // renders a Bootstrap Dropdown styled as a rail item, mirroring the Actions
  // menu in Accounts.jsx. `placement` = "right" for the desktop side-rail
  // (menu flies out to the right), "down" for the mobile top strip.
  const renderRailItem = (item, { prefix, placement }) => {
    if (item.gated && !userRole) return null;
    const railKey = `${prefix}-${item.key}`;

    if (item.type === "submenu") {
      const submenuKey = railKey;
      const sectionActive = activeSection === item.key;
      return (
        <Dropdown
          key={submenuKey}
          drop={placement === "right" ? "end" : "down"}
          align={placement === "right" ? "start" : "end"}
          show={openSubKey === submenuKey}
          onToggle={(show) => setOpenSubKey(show ? submenuKey : null)}
        >
          <Dropdown.Toggle
            as={Button}
            aria-label={item.label}
            title={item.label}
            className={
              sectionActive
                ? "acc-rail-item acc-rail-item-labeled active"
                : "acc-rail-item acc-rail-item-labeled"
            }
          >
            <i className={item.icon} />
            <span className="acc-rail-label">{item.short}</span>
          </Dropdown.Toggle>
          <Dropdown.Menu
            className="shadow-sm actions-dropdown"
            renderOnMount
            popperConfig={{
              strategy: "fixed",
              modifiers: [
                {
                  name: "computeStyles",
                  options: { adaptive: false, gpuAcceleration: false },
                },
              ],
            }}
          >
            {item.children.map((sub) => {
              const isActiveChild = sectionActive && eraFilter === sub.key;
              return (
                <Dropdown.Item
                  key={sub.key}
                  active={isActiveChild}
                  onClick={() => {
                    navigate(
                      `/billing?section=${item.key}&eraFilter=${sub.key}`,
                      { replace: true },
                    );
                    setOpenSubKey(null);
                  }}
                >
                  <i className={`${isActiveChild ? "fa-solid fa-check" : sub.icon} me-2`} />
                  {sub.label}
                </Dropdown.Item>
              );
            })}
          </Dropdown.Menu>
        </Dropdown>
      );
    }

    const isActive = activeSection === item.key;
    return (
      <Button
        key={railKey}
        aria-label={item.label}
        className={
          isActive
            ? "acc-rail-item acc-rail-item-labeled active"
            : "acc-rail-item acc-rail-item-labeled"
        }
        onClick={() => setActiveSection(item.key)}
      >
        <i className={item.icon} />
        <span className="acc-rail-label">{item.short}</span>
      </Button>
    );
  };

  // render content
  function renderContent() {
    switch (activeSection) {
      case "account":
        return <AccountList />;
      case "invoices":
        return <AgedInvoicesList />;
      case "transactions":
        return <TransactionList />;
      case "allocations":
        return <AllocationReport />;
      case "claims":
        return <ClaimList />;
      case "eras":
        return <ERAList />;
      //   case "payments":
      //     return <OzowList />;
      default:
        return <AccountList />;
    }
  }

  return (
    <div style={{ marginTop: "50px" }}>
      {/*  {renderHeader()} */}

      <Container fluid className="px-0">
        <Row className="g-0">
          {/* MOBILE TAB STRIP (<768px): horizontal-scroll icon row */}
          <Col xs={12} className="d-flex d-lg-none g-0">
            <Container className="bg-white g-0 shadow-sm rounded">
              <div className="acc-rail-mobile">
                {sidebarItems.map((item) =>
                  renderRailItem(item, { prefix: "mobile", placement: "down" }),
                )}
              </div>
            </Container>
          </Col>

          {/* LEFT PANEL: DESKTOP ICON RAIL (>=768px) */}
          <Col xs="auto" className="d-none d-lg-block g-0" style={{ width: 64 }}>
            <Container className="bg-white vh-100 g-0 p-1 shadow-sm rounded" style={{ width: 64 }}>
              <div className="acc-rail">
                {sidebarItems.map((item) =>
                  renderRailItem(item, { prefix: "desktop", placement: "right" }),
                )}
              </div>
            </Container>
          </Col>

          {/* RIGHT CONTENT */}
          <Col xs={12} md className="g-0">
            <Container fluid className="pt-3 bg-transparent pb-3 mb-3 rounded">
              <h4 className="text-primary fw-normal mb-3 ms-1">
                {sidebarItems.find((i) => i.key === activeSection)?.label}
                {activeSection === "eras" && eraFilter === "matched" && ": Matched"}
                {activeSection === "eras" && eraFilter === "unmatched" && ": Unmatched"}
              </h4>
              {renderContent()}
            </Container>
          </Col>
        </Row>
      </Container>
    </div>
  );
}

export default Billing;
